import { useState, useEffect } from 'react';
import { apiFetch, buildQueryString } from '../lib/api';
import type { KPIData, TimeseriesPoint, MetaCampaign, MetaAd } from '../types';

interface Filters {
  startDate: string;
  endDate: string;
  service: string;
  account: string;
}

interface MetaAdsData {
  kpis: KPIData | null;
  timeseries: TimeseriesPoint[];
  campaigns: MetaCampaign[];
  ads: MetaAd[];
  isLoading: boolean;
  error: string | null;
}

export function useMetaAdsData(filters: Filters): MetaAdsData {
  const [kpis, setKpis] = useState<KPIData | null>(null);
  const [timeseries, setTimeseries] = useState<TimeseriesPoint[]>([]);
  const [campaigns, setCampaigns] = useState<MetaCampaign[]>([]);
  const [ads, setAds] = useState<MetaAd[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setIsLoading(true);
    setError(null);

    const qs = buildQueryString({
      startDate: filters.startDate,
      endDate: filters.endDate,
      service: filters.service !== 'all' ? filters.service : undefined,
      account: filters.account !== 'all' ? filters.account : undefined,
    });

    Promise.all([
      apiFetch<KPIData>(`/api/metrics/kpis${qs}`),
      apiFetch<TimeseriesPoint[]>(`/api/metrics/timeseries${qs}`),
      apiFetch<MetaCampaign[]>(`/api/metrics/campaigns${qs}`),
      apiFetch<MetaAd[]>(`/api/metrics/ads${qs}`),
    ]).then(([k, t, c, a]) => {
      setKpis(k);
      setTimeseries(t);
      setCampaigns(c);
      setAds(a);
    }).catch(err => {
      setError(err instanceof Error ? err.message : 'Erro ao carregar dados');
    }).finally(() => {
      setIsLoading(false);
    });
  }, [filters.startDate, filters.endDate, filters.service, filters.account]);

  return { kpis, timeseries, campaigns, ads, isLoading, error };
}
